import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { X } from 'lucide-react';

import { hasMadeChoice, CONSENT_CHANGED_EVENT } from '../../utils/cookieConsent';
import {
  INSTALL_FLOAT_ROOM,
  INSTALL_FLOAT_ROOM_ALONE,
  INSTALL_FLOAT_VAR,
  INSTALL_PHONE_QUERY,
  WINDOW_OR_PHONE_PAGE_SELECTOR,
  WINDOW_SELECTOR,
  canOfferInstall,
  dismissedRecently,
  installBannerLook,
  installBannerPlacement,
  showsInstallBanner,
} from '../../utils/pwa';
import { useDialogOpen } from '../../utils/useDialogOpen';
import { useInstallApp } from './pwaContext';

const useCookieAnswered = () => {
  const [isAnswered, setIsAnswered] = useState(hasMadeChoice);
  useEffect(() => {
    const handler = () => setIsAnswered(hasMadeChoice());
    window.addEventListener(CONSENT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
  }, []);
  return isAnswered;
};

// Apple's Smart App Banner: ✕ at the left, the rounded app icon, three lines, the gray "Obter" pill
const IosBanner = ({ name, copy, onInstall, onDismiss }) => (
  <section className="mt-install mt-install--ios" aria-label={copy('rowLabel')} data-look="ios">
    <div className="mt-install__row">
      <button type="button" className="mt-install__close" onClick={onDismiss} aria-label={copy('dismiss')}>
        <X aria-hidden="true" />
      </button>
      <img className="mt-install__icon" src="/apple-touch-icon.png" alt="" width="60" height="60" />
      <div className="mt-install__text">
        <p className="mt-install__name">{name}</p>
        <p className="mt-install__sub">{copy('iosSubtitle')}</p>
        <p className="mt-install__detail">{copy('iosDetail')}</p>
      </div>
      <button type="button" className="mt-install__cta" onClick={onInstall}>
        {copy('iosCta')}
      </button>
    </div>
  </section>
);

// Chrome's install bar: the round icon, the name over the address, the blue "Instalar" pill, ✕ at the right
const AndroidBanner = ({ name, copy, onInstall, onDismiss }) => (
  <section className="mt-install mt-install--android" aria-label={copy('rowLabel')} data-look="android">
    <div className="mt-install__row">
      <img className="mt-install__icon" src="/icon-maskable-192.png" alt="" width="40" height="40" />
      <div className="mt-install__text">
        <p className="mt-install__name">{name}</p>
        <p className="mt-install__sub">{copy('androidSubtitle')}</p>
      </div>
      <button type="button" className="mt-install__cta" onClick={onInstall}>
        {copy('androidCta')}
      </button>
      <button type="button" className="mt-install__close" onClick={onDismiss} aria-label={copy('dismiss')}>
        <X aria-hidden="true" />
      </button>
    </div>
  </section>
);

// Android's banner floats: while it's here, everything pinned above the tab bar and the end of the page make room for it
const FloatingBanner = ({ overTabBar, children }) => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(INSTALL_FLOAT_VAR, `${overTabBar ? INSTALL_FLOAT_ROOM : INSTALL_FLOAT_ROOM_ALONE}px`);
    return () => {
      root.style.removeProperty(INSTALL_FLOAT_VAR);
    };
  }, [overTabBar]);

  return <div className={`mt-install-float${overTabBar ? ' mt-install-float--over-bar' : ''}`}>{children}</div>;
};

/**
 * "Instalar o app" on phones, from the first visit (docs/pwa.md). On an iPhone a bar at the very top of the page, above
 * the header; on Android a card fixed just above the tab bar, where Chrome shows its own, so it's on screen however far
 * the page is scrolled. Each wears the phone's own look, in its system font, apart from the app's tokens on purpose
 * (DESIGN.md, The Phone's Own Look Rule). It waits for an open window, and Android's for the cookie card (the same spot).
 *
 * overTabBar: the page has the floating tab bar under it (the signed-in pages), so it sits 8px over the pill; with none,
 * 16px over the screen's edge.
 */
export const InstallBanner = ({ overTabBar = false }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const phone = useMediaQuery({ query: INSTALL_PHONE_QUERY });
  const { platform, standalone, dismissedAt, tapped, install, dismissBanner } = useInstallApp();
  const cookieAnswered = useCookieAnswered();
  const copy = (key) => t(`components.InstallApp.${key}`);

  const look = installBannerLook(platform);
  const placement = installBannerPlacement(look);
  const mayShow = phone && !tapped && canOfferInstall(platform, standalone) && !dismissedRecently(dismissedAt);
  const windowOpen = useDialogOpen(mayShow, placement === 'top' ? WINDOW_OR_PHONE_PAGE_SELECTOR : WINDOW_SELECTOR);
  const busy = windowOpen || (placement === 'bottom' && !cookieAnswered);

  if (tapped || !showsInstallBanner({ platform, standalone, phone, dismissedAt, pathname, busy })) return null;

  const actions = { name: t('appName'), copy, onInstall: () => install('banner'), onDismiss: dismissBanner };
  if (look === 'ios') return <IosBanner {...actions} />;
  return (
    <FloatingBanner overTabBar={overTabBar}>
      <AndroidBanner {...actions} />
    </FloatingBanner>
  );
};
